import { iamEndpoints } from './endpoints';

const USER_FETCH_PERMISSION = 'user.fetch';

// One page of users carries every user's full policy tree, so the page is the endpoint default
// (100), not its maximum (500): a company with more users is read page by page.
const COMPANY_USERS_PAGE_SIZE = 100;

// rest-api's shared 403 body (`ACCESS_DENIED_DETAIL`, api/auth/principal.py). Istio's ext_authz
// also answers 403 for a missing, expired or invalid token, with a plain-text body and no
// `detail`: that one is a session problem, not a missing permission, and must keep failing loudly.
const ACCESS_DENIED_DETAIL = 'You do not have access to this resource.';

const isAccessDeniedError = (error) => {
	return error?.response?.status === 403 && error?.response?.data?.detail === ACCESS_DENIED_DETAIL;
};

// The fields the GraphQL query `usersByCompanyId` used to select, which is the contract the Hub
// reads. There is no schema or type generation between rest-api and the Hub: this list and the
// key-set assertions in rest-api's test_iam_company_users_principal_boundary.py are the only
// guards, so change both sides together.
//   id email lastUsed
//   policies { resource { id type referenceId }
//              bindings { userIds role { id name permissions { id name } } } }
//   profile { id firstName lastName phoneNumber roleInTheCompany hasVirtualApplianceAccess
//             seenWelcomeBanner isSupportContact subscribeToNews subscribeToProductNews
//             subscribeToSatisfactionSurveys dataCollectionConsent }
// rest-api returns lastUsed as an ISO-8601 instant (or null); ConvertTimestampToDateTime wants
// epoch milliseconds, which is what the GraphQL field carried, so the conversion lives here and
// no component changes. The instant has no millisecond part, so the Hub shows the same minute as
// before. A value Date.parse cannot read becomes null ("N/A"), not NaN.
const mapCompanyUser = (companyUser) => {
	const parsedLastUsed = companyUser.lastUsed ? Date.parse(companyUser.lastUsed) : null;

	return {
		...companyUser,
		lastUsed: Number.isNaN(parsedLastUsed) ? null : parsedLastUsed,
	};
};

export class CompanyUserService {
	constructor(restAxios, accessService) {
		this.restAxios = restAxios;
		this.accessService = accessService;
	}

	// The one way every screen reads the company users. Resolves to
	// `{ users, accessDenied }` and never throws for a missing permission:
	//   - a caller who does not hold user.fetch on the company is answered locally, no request is
	//     sent (it would only come back as a 403);
	//   - if the client-side policies were stale and rest-api still answers with its own 403, that
	//     is treated the same way.
	// Any other failure, including the ext_authz 403 of an expired session, is rethrown.
	async getCompanyUsers(companyId) {
		if (!this.accessService.hasPermissionOnCompanyResource(USER_FETCH_PERMISSION)) {
			return this.denied(companyId);
		}

		try {
			return { users: await this.fetchAllPages(companyId), accessDenied: false };
		} catch (error) {
			if (isAccessDeniedError(error)) {
				return this.denied(companyId);
			}

			throw error;
		}
	}

	denied(companyId) {
		console.warn(`Company users are not available to this account (company ${companyId}).`);

		return { users: [], accessDenied: true };
	}

	async fetchAllPages(companyId) {
		const users = [];
		let start = 0;
		let totalItems = 0;

		do {
			const response = await this.restAxios.get(iamEndpoints.users, {
				params: { companyId, start, limit: COMPANY_USERS_PAGE_SIZE },
			});
			const { items, pagination } = response.data;

			// totalItems counts profiles: a profile without an IAM user leaves a page short or empty, and
			// later pages can still hold users, so only start and totalItems end the loop.
			users.push(...items.map(mapCompanyUser));
			totalItems = pagination.totalItems;
			start += COMPANY_USERS_PAGE_SIZE;
		} while (start < totalItems);

		return users;
	}
}
